import { useMemo, useState } from 'react';
import { useAuth } from '../../lib/auth';
import { can, canEditOwnDraft, canViewRecordAudit } from '../../lib/permissions';
import { api, ApiError } from '../../lib/api';
import { useDetail } from '../../lib/useDetail';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { KeyValue, LineTable, Pill, Spinner } from '../../components/ui';
import { Field, TextInput } from '../../components/ui';
import { SearchSelect } from '../../components/SearchSelect';
import { dateLabel, money, num, quotationStatus } from '../../lib/format';
import { QUOTATION_ACTIONS, type QuotationAction } from '../../lib/sales';
import { openAuthenticatedPrint } from '../../lib/print';
import type { Item, Quotation, QuotationLine, RecordAudit } from '../../lib/types';

function ActionError({ message }: { message: string }) {
  return <div className="notice bad" style={{ marginTop: 10 }}>{message}</div>;
}

// 转合同预览 + 确认（报价被接受后）。可编辑数量/单价、排除行（部分确认）。
function ConvertModal({ rec, onClose, onDone }: {
  rec: Quotation;
  onClose: () => void;
  onDone: (contractNumber: string) => void;
}) {
  const [contractDate, setContractDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [lines, setLines] = useState(() => (rec.lines ?? []).map((l) => ({
    id: l.id,
    item_id: l.item_id,
    description_es: l.description_es,
    quantity: Number(l.quantity),
    uom: l.uom,
    unit_price: Number(l.unit_price),
    tax_rate: Number(l.tax_rate ?? 21),
    keep: true,
  })));
  const kept = lines.filter((l) => l.keep && l.quantity > 0);
  const t = useMemo(() => {
    const subtotal = kept.reduce((s, l) => s + l.quantity * l.unit_price, 0);
    const tax = kept.reduce((s, l) => s + l.quantity * l.unit_price * l.tax_rate / 100, 0);
    return { subtotal, tax_total: tax, grand_total: subtotal + tax };
  }, [lines]);

  const submit = async () => {
    setErr(null);
    if (!contractDate) { setErr('合同日期为必填'); return; }
    if (!kept.length) { setErr('请至少保留一行明细'); return; }
    setBusy(true);
    try {
      const res = await api.post<{ data: { number: string } }>(`/api/quotations/${rec.id}/convert`, {
        contract_date: contractDate,
        delivery_due_date: dueDate || null,
        terms_es: rec.terms_es || null,
        lines: kept.map((l) => ({
          item_id: l.item_id,
          description_es: l.description_es,
          quantity: l.quantity,
          uom: l.uom,
          unit_price: l.unit_price,
          tax_rate: l.tax_rate,
        })),
      });
      onDone(res.data.number);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '转合同失败');
    } finally { setBusy(false); }
  };

  return (
    <div className="overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="modal" role="dialog" aria-modal="true" aria-label="转销售合同">
        <div className="modal-head">转为销售合同 · {rec.number}</div>
        <div className="modal-body">
          <div className="modal-msg">可改数量/单价、去掉不要的行（部分确认）。行描述/税率沿用报价，后续报价修改不影响合同。</div>
          <div style={{ margin: '12px 0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--panel-inset)' }}>
                  <th style={{ padding: 6, width: 36 }}></th>
                  <th style={{ textAlign: 'left', padding: 6 }}>描述</th>
                  <th style={{ padding: 6, width: 80 }}>数量</th>
                  <th style={{ padding: 6, width: 100 }}>净单价</th>
                  <th style={{ padding: 6, width: 60 }}>IVA%</th>
                  <th style={{ padding: 6, width: 100 }}>行金额</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={l.id || i} style={{ borderTop: '1px solid var(--line-soft)', opacity: l.keep ? 1 : 0.45 }}>
                    <td style={{ padding: 6 }}>
                      <input type="checkbox" checked={l.keep} onChange={(e) => {
                        const next = [...lines]; next[i] = { ...l, keep: e.target.checked }; setLines(next);
                      }} />
                    </td>
                    <td style={{ padding: 6 }}>
                      <SearchSelect<Item>
                        endpoint="/api/items"
                        value={l.item_id ?? null}
                        label=""
                        limit={15}
                        ariaLabel="选择物料"
                        placeholder="编码 / 名称…"
                        optionTitle={(i) => i.name_es || i.name_zh}
                        optionMeta={(i) => i.code}
                        onChange={(it) => {
                          if (!it) return;
                          const n = [...lines];
                          n[i] = { ...l, item_id: it.id, description_es: it.name_es || it.name_zh || l.description_es, unit_price: Number(it.sale_price || l.unit_price) };
                          setLines(n);
                        }}
                      />
                    </td>
                    <td style={{ padding: 6 }}>{l.description_es}</td>
                    <td style={{ padding: 4 }}>
                      <input className="txt" type="number" min={0} value={l.quantity} disabled={!l.keep}
                        onChange={(e) => { const n = [...lines]; n[i] = { ...l, quantity: Number(e.target.value) || 0 }; setLines(n); }} />
                    </td>
                    <td style={{ padding: 4 }}>
                      <input className="txt" type="number" min={0} step={0.01} value={l.unit_price} disabled={!l.keep}
                        onChange={(e) => { const n = [...lines]; n[i] = { ...l, unit_price: Number(e.target.value) || 0 }; setLines(n); }} />
                    </td>
                    <td style={{ padding: 4 }}>
                      <input className="txt" type="number" min={0} value={l.tax_rate} disabled={!l.keep}
                        onChange={(e) => { const n = [...lines]; n[i] = { ...l, tax_rate: Number(e.target.value) || 0 }; setLines(n); }} />
                    </td>
                    <td style={{ padding: 6, textAlign: 'right' }}>{money(l.quantity * l.unit_price, rec.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="editor-totals" style={{ justifyContent: 'flex-end' }}>
            <div className="t"><span className="k">净额</span><span className="v">{money(t.subtotal, rec.currency)}</span></div>
            <div className="t"><span className="k">IVA</span><span className="v">{money(t.tax_total, rec.currency)}</span></div>
            <div className="t grand"><span className="k">含税总额</span><span className="v">{money(t.grand_total, rec.currency)}</span></div>
          </div>
          <FormGrid2>
            <Field label="合同日期" required>
              <TextInput type="date" value={contractDate} onChange={(e) => setContractDate(e.target.value)} />
            </Field>
            <Field label="交付到期日" hint="可留空">
              <TextInput type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </Field>
          </FormGrid2>
          {err ? <div className="notice bad" style={{ marginTop: 10 }}>{err}</div> : null}
        </div>
        <div className="modal-foot">
          <div className="spacer" />
          <button className="btn" onClick={onClose} disabled={busy}>返回</button>
          <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '生成中…' : `确认生成合同（${kept.length} 行）`}</button>
        </div>
      </div>
    </div>
  );
}

function FormGrid2({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 12, marginTop: 12 }}>{children}</div>;
}

export function QuotationDetail({ row, onEdit, onChanged }: {
  row: Quotation;
  onEdit: (q: Quotation) => void;
  onChanged: () => void;
}) {
  const { user } = useAuth();
  const { data, loading, error, reload } = useDetail<Quotation>(`/api/quotations/${row.id}`);
  const rec = data ?? row;
  const st = quotationStatus(rec.status);
  const canWrite = can(user, 'quotations', 'create');
  const canPrint = can(user, 'quotations', 'print');
  const canAudit = canViewRecordAudit(user);
  const isOwnerDraft = rec.status === 'draft' && canEditOwnDraft(user, 'quotations', rec);

  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<QuotationAction | null>(null);
  const [convertOpen, setConvertOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [printing, setPrinting] = useState(false);
  const audit = useDetail<RecordAudit>(auditOpen ? `/api/audit/quotations/${row.id}` : null);

  const actions = QUOTATION_ACTIONS[rec.status] ?? [];
  const hasDiscount = (rec.lines ?? []).some((l) => (l.discount_percentage ?? 0) > 0 || (l.discount_amount ?? 0) > 0 || (l.list_price ?? 0) > l.unit_price);

  const doTransition = async (action: QuotationAction, reason: string) => {
    setBusy(true); setActionErr(null); setNotice(null);
    try {
      await api.post(`/api/quotations/${rec.id}/status`, { status: action.to, reason: reason || null });
      setNotice(`已变更为「${st2(action.to)}」，状态与操作人已写入审计。`);
      setConfirm(null);
      reload(); onChanged();
    } catch (e) {
      setActionErr(e instanceof ApiError ? e.message : '状态变更失败');
      setConfirm(null);
    } finally { setBusy(false); }
  };

  const simplePost = async (path: string, label: string) => {
    setBusy(true); setActionErr(null); setNotice(null);
    try {
      const res = await api.post<{ data: Quotation }>(path);
      setNotice(`${label}：已生成 ${res.data.number}（草稿），可在列表中打开继续编辑。`);
      onChanged();
    } catch (e) {
      setActionErr(e instanceof ApiError ? e.message : `${label}失败`);
    } finally { setBusy(false); }
  };

  const doPrint = async () => {
    setPrinting(true); setActionErr(null);
    const r = await openAuthenticatedPrint('quotation', rec.id);
    if (!r.ok) setActionErr(r.message || '打印失败');
    setPrinting(false);
  };

  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id">{rec.number}</span>
        <h3>{rec.customer_name || '客户'}</h3>
        <Pill tone={st.tone} label={st.label} />
        {rec.version && rec.version > 1 ? <span className="pill neutral">版本 v{rec.version}</span> : null}
        <div className="spacer" />
        {loading ? <Spinner label="加载明细…" /> : null}
      </div>
      {error ? <div className="notice bad">{error}</div> : null}
      {notice ? <div className="notice info">{notice}</div> : null}
      {actionErr ? <ActionError message={actionErr} /> : null}

      <div className="kv-grid">
        <KeyValue k="报价日期">{dateLabel(rec.quotation_date)}</KeyValue>
        <KeyValue k="有效期至">{dateLabel(rec.valid_until)}</KeyValue>
        <KeyValue k="客户税号">{rec.customer_tax_id || '—'}</KeyValue>
        <KeyValue k="币种">{rec.currency}</KeyValue>
        <KeyValue k="净额">{money(rec.subtotal, rec.currency)}</KeyValue>
        <KeyValue k="IVA 税额">{money(rec.tax_total, rec.currency)}</KeyValue>
        <KeyValue k="含税总额">{money(rec.grand_total, rec.currency)}</KeyValue>
        {rec.revision_of ? <KeyValue k="修订自">（版本链）</KeyValue> : null}
      </div>

      {rec.notes_zh ? <div className="notice info">备注：{rec.notes_zh}</div> : null}
      {rec.terms_es ? <div className="notice" style={{ background: 'var(--panel-inset)' }}>西语条款：{rec.terms_es}</div> : null}

      <LineTable<QuotationLine>
        rows={rec.lines ?? []}
        columns={[
          { header: '描述', render: (l) => l.description_es },
          { header: '数量', align: 'right', render: (l) => <span className="num">{num(l.quantity, 3)}</span> },
          { header: '单位', render: (l) => <span className="muted">{l.uom}</span> },
          ...(hasDiscount ? [
            { header: '原价', align: 'right' as const, render: (l: QuotationLine) => <span className="num muted">{l.list_price ? money(l.list_price, rec.currency) : '—'}</span> },
            { header: '折扣', align: 'right' as const, render: (l: QuotationLine) => <span className="num muted">{(l.discount_percentage ?? 0) > 0 ? `${num(l.discount_percentage, 0)}%` : (l.discount_amount ?? 0) > 0 ? money(l.discount_amount, rec.currency) : '—'}</span> },
          ] : []),
          { header: '净单价', align: 'right', render: (l) => <span className="num">{money(l.unit_price, rec.currency)}</span> },
          { header: 'IVA', align: 'right', render: (l) => <span className="num">{num(l.tax_rate, 0)}%</span> },
          { header: '行金额', align: 'right', render: (l) => <span className="num strong">{money(l.line_total, rec.currency)}</span> },
        ]}
      />

      {canWrite ? (
        <div className="detail-actions" style={{ flexWrap: 'wrap', marginTop: 4 }}>
          {rec.status === 'draft' && isOwnerDraft ? (
            <button className="btn primary" disabled={busy || loading} onClick={() => onEdit(rec)}>编辑草稿</button>
          ) : null}
          {rec.status === 'accepted' ? (
            <button className="btn primary" disabled={busy} onClick={() => setConvertOpen(true)}>转为销售合同</button>
          ) : null}
          {actions.map((a) => (
            <button
              key={a.to}
              className={`btn ${a.primary ? 'primary' : ''}`}
              disabled={busy}
              title={a.adminOnly ? '该流转需管理员权限（点击后由服务端校验）' : undefined}
              onClick={() => { setActionErr(null); if (a.confirm) setConfirm(a); else void doTransition(a, ''); }}
            >
              {a.label}{a.adminOnly && user?.role !== 'admin' ? '（需管理员）' : ''}
            </button>
          ))}
          <div className="spacer" />
          <button className="btn" disabled={busy} onClick={() => void simplePost(`/api/quotations/${rec.id}/copy`, '复制')}>复制</button>
          <button className="btn" disabled={busy} onClick={() => void simplePost(`/api/quotations/${rec.id}/new-version`, '新建版本')}>新版本</button>
          {canPrint ? <button className="btn" disabled={printing} onClick={() => void doPrint()}>{printing ? '准备打印…' : '西语打印 / PDF'}</button> : null}
          {canAudit ? <button className="btn link" onClick={() => setAuditOpen((v) => !v)}>{auditOpen ? '收起审计' : '审计记录'}</button> : null}
        </div>
      ) : (
        <div className="detail-actions">
          {canPrint ? <button className="btn" disabled={printing} onClick={() => void doPrint()}>{printing ? '准备打印…' : '西语打印 / PDF'}</button> : null}
          {canAudit ? <button className="btn link" onClick={() => setAuditOpen((v) => !v)}>{auditOpen ? '收起审计' : '审计记录'}</button> : null}
          <span className="muted" style={{ fontSize: 12 }}>当前角色只读（写权限：销售 / 管理员；打印：销售 / 财务）。</span>
        </div>
      )}

      {auditOpen ? (
        <div className="panel" style={{ boxShadow: 'none' }}>
          <div className="panel-body">
            {audit.loading ? <Spinner label="读取审计留痕…" /> : null}
            {audit.error ? <div className="notice bad">{audit.error}</div> : null}
            {audit.data ? (
              <>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>状态流转</div>
                {audit.data.status.length === 0 ? <div className="muted" style={{ fontSize: 12.5 }}>暂无状态变更记录。</div> : (
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {audit.data.status.map((s) => {
                      const from = s.from_status ? quotationStatus(s.from_status).label : '创建';
                      const to = quotationStatus(s.to_status).label;
                      return (
                        <li key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                          <Pill tone={quotationStatus(s.to_status).tone} label={to} />
                          <span className="muted">{from} → {to}</span>
                          {s.reason ? <span>· {s.reason}</span> : null}
                          <span className="spacer" style={{ marginLeft: 'auto', fontSize: 11 }}>
                            {s.changed_by_name || '系统'} · {new Date(s.created_at).toLocaleString('zh-CN')}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>操作留痕（含改前 / 改后）</div>
                {audit.data.audit.length === 0 ? <div className="muted" style={{ fontSize: 12.5 }}>暂无操作日志。</div> : (
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {audit.data.audit.map((a) => (
                      <li key={a.id} style={{ fontSize: 12.5, borderBottom: '1px solid var(--line-soft)', paddingBottom: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="num strong">{a.action}</span>
                          <span className="muted" style={{ marginLeft: 'auto', fontSize: 11 }}>
                            {a.operator_name || '内部服务'} · {new Date(a.created_at).toLocaleString('zh-CN')}
                          </span>
                        </div>
                        {a.payload ? <div className="muted" style={{ fontSize: 11.5, marginTop: 2, wordBreak: 'break-all' }}>
                          {JSON.stringify(a.payload)}
                        </div> : null}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      {confirm ? (
        <ConfirmDialog
          open
          title={`${confirm.label} · ${rec.number}`}
          message={<>{`确认将报价单「${rec.number}」变更为「${st2(confirm.to)}」？`}</>}
          detail="该操作会写入操作人、时间与改前/改后状态，并触发服务端权限校验。"
          confirmLabel={confirm.label}
          tone={confirm.tone === 'bad' ? 'danger' : 'primary'}
          requireReason={confirm.to === 'cancelled' || confirm.to === 'rejected'}
          reasonPlaceholder="例如：客户预算调整、需求取消…"
          busy={busy}
          onConfirm={(reason) => void doTransition(confirm, reason)}
          onCancel={() => setConfirm(null)}
        />
      ) : null}

      {convertOpen ? (
        <ConvertModal
          rec={rec}
          onClose={() => setConvertOpen(false)}
          onDone={(contractNumber) => {
            setConvertOpen(false);
            setNotice(`已生成销售合同 ${contractNumber}。`);
            reload(); onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

function st2(status: string): string {
  return quotationStatus(status).label;
}
